import React, { useEffect, useMemo, useRef, useState } from "react";
import DOMPurify from "dompurify";
import "../../assets/styles/buyer-brief.less";
import { useParams, useNavigate } from "react-router-dom";
import { Button, Card, Spin, Result, ToolbarButton, Typography } from "../../design-system";
import { DownloadOutlined, LoadingOutlined, ReloadOutlined } from "@ant-design/icons";
import {
    BuyerBrief,
    BuyerBriefJob,
    BriefStatus,
    BriefJobStatus,
} from "../../types/BuyerBriefTypes";
import { getBriefById, getBriefStatus } from "../../common/api/BuyerBriefClient";
import { SvgIcon } from "../../common/components/SvgIcon";
import { useBriefStickyNav } from "../../common/useBriefStickyNav";
import { useBriefPrint } from "../../common/useBriefPrint";
import classes from "./BuyerBriefView.module.less";

const { Text } = Typography;

// Coarse fallback only. The server writes a customer-facing `currentStep` on
// the job ("Gathering sources", "Analyzing findings") which is strictly more
// specific, so these are used only before the first poll lands and for a
// status that somehow carries no step. Kept in the same register as the step
// strings — no trailing ellipses — so the two never look like different
// vocabularies on the same screen.
const JOB_STEP_LABELS: Record<string, string> = {
    [BriefJobStatus.QUEUED]: "Queued",
    [BriefJobStatus.RESEARCHING]: "Researching",
    [BriefJobStatus.VERIFYING]: "Checking accuracy",
    [BriefJobStatus.CORRECTING]: "Resolving inconsistencies",
    [BriefJobStatus.RENDERING]: "Assembling your brief",
};

// Internal-methodology sections that briefs generated before mid-2026 carry
// baked into their stored HTML. New generations no longer emit them; stripping
// at display time makes legacy briefs match without a regeneration and acts as
// a safety net if a render ever emits them again.
const INTERNAL_SECTION_IDS = ["data-confidence", "confidence-analysis", "sources"];

function stripInternalSections(html: string): string {
    if (!html) return html;
    const doc = new DOMParser().parseFromString(html, "text/html");
    for (const id of INTERNAL_SECTION_IDS) {
        doc.getElementById(id)?.remove();
        doc.querySelectorAll(`a[href="#${id}"]`).forEach((a) => a.remove());
    }
    return doc.body.innerHTML;
}

const BuyerBriefView: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [brief, setBrief] = useState<BuyerBrief | null>(null);
    const [jobStatus, setJobStatus] = useState<BuyerBriefJob | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<{
        type: "not_found" | "network" | "timeout";
        message: string;
    } | null>(null);
    const htmlContainerRef = useRef<HTMLDivElement>(null);
    const printBrief = useBriefPrint(`buyer-brief-${brief?.supplierName || id}`);

    useEffect(() => {
        const abortController = new AbortController();
        let cancelled = false;

        const POLL_INTERVALS_MS = [2000, 5000, 10000, 15000, 30000];
        const MAX_POLL_ELAPSED_MS = 15 * 60 * 1000;

        let elapsedMs = 0;
        let pollCount = 0;
        let pollTimer: ReturnType<typeof setTimeout> | null = null;

        const poll = async () => {
            const interval = POLL_INTERVALS_MS[Math.min(pollCount, POLL_INTERVALS_MS.length - 1)];
            pollCount++;
            elapsedMs += interval;

            if (elapsedMs >= MAX_POLL_ELAPSED_MS) {
                if (!cancelled) {
                    setError({
                        type: "timeout",
                        message:
                            "Research is taking longer than expected. Please refresh to check status.",
                    });
                }
                return;
            }

            try {
                const status = await getBriefStatus(Number(id), { signal: abortController.signal });
                if (cancelled) return;
                setJobStatus(status);

                if (
                    status?.status === BriefJobStatus.COMPLETED ||
                    status?.status === BriefJobStatus.FAILED
                ) {
                    loadBrief(true);
                    return;
                }
            } catch {
                // Ignore polling errors
            }

            pollTimer = setTimeout(poll, interval);
        };

        const startPolling = () => {
            if (pollTimer) return;
            pollTimer = setTimeout(poll, POLL_INTERVALS_MS[0]);
        };

        // `silent` skips the full-page Spin. The completion re-fetch used to
        // raise it, so a brief that finished while you watched flashed a
        // spinner over itself before showing the result.
        const loadBrief = async (silent = false) => {
            if (!id) return;
            if (!silent) setLoading(true);
            setError(null);
            try {
                const data = await getBriefById(Number(id), { signal: abortController.signal });
                if (cancelled) return;
                setBrief(data);

                if (data.status === BriefStatus.PROCESSING || data.status === BriefStatus.QUEUED) {
                    startPolling();
                }
            } catch (err: any) {
                if (cancelled) return;
                if (err?.status === 404 || err?.response?.status === 404) {
                    setError({ type: "not_found", message: "Brief not found" });
                } else if (err?.name !== "AbortError") {
                    setError({
                        type: "network",
                        message: "Failed to load brief. Please try again.",
                    });
                }
            } finally {
                if (!cancelled && !silent) setLoading(false);
            }
        };

        loadBrief();

        return () => {
            cancelled = true;
            abortController.abort();
            if (pollTimer) {
                clearTimeout(pollTimer);
                pollTimer = null;
            }
        };
    }, [id]);

    const cleanHtml = useMemo(() => {
        // FORBID_TAGS: ["style"] blocks injected <style> blocks; inline style= attributes
        // are intentionally allowed for brief layout. DOMPurify strips all on* event
        // handler attributes by default — no manual FORBID_ATTR list needed.
        const sanitized = DOMPurify.sanitize(brief?.briefHtml || "", {
            ADD_ATTR: ["target", "rel"],
            FORCE_BODY: true,
            WHOLE_DOCUMENT: false,
            FORBID_TAGS: ["style"],
        });
        return stripInternalSections(sanitized);
    }, [brief?.briefHtml]);

    useBriefStickyNav(htmlContainerRef, {
        enabled: brief?.status === BriefStatus.COMPLETED && !!cleanHtml,
        contentHtml: cleanHtml,
        navLinkSelector: ".sidebar a",
    });

    if (loading) {
        return (
            <div style={{ display: "flex", justifyContent: "center", padding: 100 }}>
                <Spin size="large" />
            </div>
        );
    }

    if (error) {
        if (error.type === "not_found") {
            return <Result status="404" title="Brief not found" />;
        }
        if (error.type === "timeout") {
            return (
                <Result
                    status="info"
                    title="Still running"
                    subTitle={error.message}
                    extra={
                        <Button icon={<ReloadOutlined />} onClick={() => navigate(0)}>
                            Refresh
                        </Button>
                    }
                />
            );
        }
        return (
            <Result
                status="warning"
                title="Unable to load brief"
                subTitle={error.message}
                extra={
                    <Button icon={<ReloadOutlined />} onClick={() => navigate(0)}>
                        Retry
                    </Button>
                }
            />
        );
    }

    if (!brief) {
        return <Result status="404" title="Brief not found" />;
    }

    const renderProcessing = () => {
        // The job's own step wins. The status map used to win, and because it
        // has an entry for every non-terminal status the step was unreachable —
        // so this page said "Conducting AI research..." for both "Gathering
        // sources (2 of 5 research areas)" and "Analyzing findings", while the
        // index showed the real phase for the same brief at the same instant.
        const stepLabel =
            jobStatus?.currentStep ||
            (jobStatus && JOB_STEP_LABELS[jobStatus.status]) ||
            "Starting";
        const stalled = brief.isStuck ?? false;

        return (
            <div style={{ textAlign: "center", padding: 80 }}>
                <Spin indicator={<LoadingOutlined style={{ fontSize: 48 }} />} />
                <h3 style={{ marginTop: 24 }}>Generating Brief for {brief.supplierName}</h3>
                <p style={{ color: stalled ? undefined : "#666", marginTop: 8 }}>
                    {/* The index marks a wedged brief; this page showed an
                        animated spinner and a live-looking phase indefinitely. */}
                    {stalled ? <Text type="warning">Stalled at: {stepLabel}</Text> : stepLabel}
                </p>
            </div>
        );
    };

    const renderFailed = () => (
        <Result
            status="error"
            title="Brief Generation Failed"
            subTitle={brief.errorDetails?.message || "An error occurred during research."}
            extra={<Button onClick={() => navigate("/briefs")}>Back to Briefs</Button>}
        />
    );

    const renderCompleted = () => (
        <div
            ref={htmlContainerRef}
            className="il-buyer-brief"
            dangerouslySetInnerHTML={{ __html: cleanHtml }}
        />
    );

    return (
        <>
            <Card
                className={classes.detailCard}
                // Back link only. The supplier name used to sit beside it and
                // was a strict duplicate: the brief's own header, pinned one
                // line below inside the same visual block, carries the
                // supplier logo and the name as its <h1>.
                title={
                    <div className={classes.headerTitle}>
                        {/* The label says where you land; the icon carries
                            "back". Uses the design-system icon set rather than
                            an antd glyph, and reads as a link, matching the
                            design. */}
                        <Button
                            className={classes.backLink}
                            type="link"
                            size="small"
                            icon={<SvgIcon name="back" size={14} />}
                            onClick={() => navigate("/briefs")}
                        >
                            All Buyer Briefs
                        </Button>
                    </div>
                }
                extra={
                    <ToolbarButton icon={<DownloadOutlined />} onClick={printBrief}>
                        Export
                    </ToolbarButton>
                }
            >
                {brief.status === BriefStatus.COMPLETED && renderCompleted()}
                {brief.status === BriefStatus.PROCESSING && renderProcessing()}
                {brief.status === BriefStatus.FAILED && renderFailed()}
                {brief.status === BriefStatus.QUEUED && renderProcessing()}
            </Card>
        </>
    );
};

export default BuyerBriefView;
